// SPDX-License-Identifier: AGPL-3.0-or-later
import * as Modal from '@app/features/app/components/dialogs/Modal';
import {CommandBlock, useNow} from '@app/features/konvo_agents/components/AgentBits';
import {ProviderLogo} from '@app/features/konvo_agents/components/AgentBrand';
import {
	CONNECTIONS_TITLE,
	connectionExplanation,
	connectionLabel,
	MACHINE_OFFLINE,
} from '@app/features/konvo_agents/components/AgentConnectionCopy';
import styles from '@app/features/konvo_agents/components/AgentConnections.module.css';
import {agentProblemMessage} from '@app/features/konvo_agents/components/AgentsCopy';
import {connectionNeedsAttention, machineConnections} from '@app/features/konvo_agents/state/AgentConnections';
import Agents from '@app/features/konvo_agents/state/Agents';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import type {AgentConnection, AgentMachineView} from '@konvo/schema/src/domains/konvo/Agents';
import {Trans, useLingui} from '@lingui/react/macro';
import {IconRefresh} from '@tabler/icons-react';
import {observer} from 'mobx-react-lite';
import {useRef, useState} from 'react';

const KEY = 'konvo-agent-connections';

export function ConnectionCard({machine, connection}: {machine: AgentMachineView; connection: AgentConnection}) {
	const {t, i18n} = useLingui();
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const now = useNow(60_000);
	const name = connection.providerName;
	const count = connection.models.length;
	const machineName = machine.name;
	const checked = i18n.date(connection.checkedAt, {dateStyle: 'medium', timeStyle: 'short'});
	const expiry = connection.loginExpiresAt
		? i18n.date(connection.loginExpiresAt, {dateStyle: 'medium', timeStyle: 'short'})
		: null;
	const canCheck =
		machine.online &&
		machine.tools.some((tool) => tool.can.connections) &&
		['claudeAgent', 'codex'].includes(connection.driver);
	const recheck = async () => {
		setBusy(true);
		setError(null);
		try {
			await Agents.checkAgentSetup(machine.id, {
				provider: connection.driver === 'codex' ? 'codex' : 'claudeAgent',
				instanceId: connection.provider,
			});
		} catch (e) {
			setError(agentProblemMessage(i18n, e));
		} finally {
			setBusy(false);
		}
	};
	return (
		<section className={styles.card} data-flx="konvo-agents.connections.account" data-provider={connection.provider}>
			<header className={styles.header}>
				<ProviderLogo provider={connection.provider} size={20} />
				<strong>{name}</strong>
				<span className={styles.status} data-attention={!machine.online || connectionNeedsAttention(connection, now)}>
					{machine.online ? connectionLabel(i18n, connection.status) : i18n._(MACHINE_OFFLINE)}
				</span>
			</header>
			<p>
				{machine.online
					? connectionExplanation(i18n, connection.status)
					: t`Reconnect ${machineName} to check this account. The status below is from its last report.`}
			</p>
			<dl className={styles.facts}>
				<div>
					<dt>
						<Trans>Connection</Trans>
					</dt>
					<dd>{connection.provider}</dd>
				</div>
				<div>
					<dt>
						<Trans>Tool</Trans>
					</dt>
					<dd>
						{connection.driver === 'claudeAgent'
							? 'Claude Code'
							: connection.driver === 'codex'
								? 'Codex'
								: connection.driver}
					</dd>
				</div>
				<div>
					<dt>
						<Trans>Last reported</Trans>
					</dt>
					<dd>{checked}</dd>
				</div>
			</dl>
			{expiry && (
				<p className={styles.expiry} data-flx="konvo-agents.connections.expiry">
					<Trans>
						Saved login expires: {expiry}. Automatic access-token refresh does not guarantee renewal of this login.
					</Trans>
				</p>
			)}
			{connection.status === 'ready' && !connection.usageAvailable && (
				<p>
					<Trans>Usage data is unavailable. This account is still connected.</Trans>
				</p>
			)}
			<details className={styles.models}>
				<summary>
					<Trans>Configured models ({count})</Trans>
				</summary>
				{connection.models.length ? (
					<ul>
						{connection.models.map((model) => (
							<li key={model.model}>
								{model.name} <code>{model.model}</code>
							</li>
						))}
					</ul>
				) : (
					<p>
						<Trans>No model catalog was returned. The account remains configured.</Trans>
					</p>
				)}
			</details>
			<details className={styles.help} open={connection.status !== 'ready'}>
				<summary>
					<Trans>Diagnose and reconnect</Trans>
				</summary>
				<p>
					<Trans>Run these commands on {machineName}. They use this account’s configuration directory.</Trans>
				</p>
				{connection.checkCommand && (
					<>
						<strong className={styles.label}>
							<Trans>Check local sign-in</Trans>
						</strong>
						<CommandBlock lines={[connection.checkCommand]} wrap />
					</>
				)}
				{connection.loginCommand && (
					<>
						<strong className={styles.label}>
							<Trans>Sign in to {name}</Trans>
						</strong>
						<CommandBlock lines={[connection.loginCommand]} wrap />
						<p>
							<Trans>
								Select the account that belongs to this connection in your browser. Then return here and check again.
							</Trans>
						</p>
					</>
				)}
				{!connection.checkCommand && (
					<p>
						<Trans>Open this connection in T3 Code to inspect its custom environment and sign-in settings.</Trans>
					</p>
				)}
				{connection.driver === 'claudeAgent' && (
					<p>
						<a
							href="https://code.claude.com/docs/en/authentication#renew-an-expiring-login"
							target="_blank"
							rel="noreferrer"
						>
							<Trans>About Claude login renewal</Trans>
						</a>
					</p>
				)}
			</details>
			{canCheck && (
				<button
					type="button"
					className={styles.check}
					disabled={busy}
					onClick={() => void recheck()}
					data-flx="konvo-agents.connections.check"
				>
					<IconRefresh size={16} />
					{busy ? <Trans>Checking connection…</Trans> : <Trans>Check again</Trans>}
				</button>
			)}
			{error && <p role="alert">{error}</p>}
		</section>
	);
}

export const AgentConnectionsDialog = observer(({machineId, onClose}: {machineId: string; onClose: () => void}) => {
	const {t, i18n} = useLingui();
	const initialFocusRef = useRef<HTMLParagraphElement>(null);
	const machine = Agents.machines.find((m) => m.id === machineId);
	const connections = machineConnections(machine ?? null);
	return (
		<Modal.Root
			size="medium"
			centered
			initialFocusRef={initialFocusRef}
			onClose={onClose}
			data-flx="konvo-agents.connections.root"
		>
			<Modal.Header title={i18n._(CONNECTIONS_TITLE)} onClose={onClose} />
			<Modal.Content>
				<div className={styles.content}>
					<p ref={initialFocusRef} tabIndex={-1}>
						<Trans>
							Configured accounts stay here when they are signed out, disabled or unavailable. Remove an account in T3
							Code to remove it here.
						</Trans>
					</p>
					{machine && (
						<p className={styles.machine}>
							{machine.name}
							{machine.online ? '' : ` · ${i18n._(MACHINE_OFFLINE)}`}
						</p>
					)}
					{machine &&
						connections.map((connection) => (
							<ConnectionCard key={connection.provider} machine={machine} connection={connection} />
						))}
					{machine?.toolProblem && (
						<p role="status">
							<code>{machine.toolProblem.message}</code>
						</p>
					)}
					{connections.length === 0 && (
						<p>
							{machine
								? machine.tools.some((tool) => tool.connections !== undefined)
									? t`No accounts are configured. Add an account in T3 Code.`
									: t`Update the Konvo Agents connector on this machine to see its configured accounts and connection diagnostics.`
								: t`This machine is no longer connected.`}
						</p>
					)}
				</div>
			</Modal.Content>
		</Modal.Root>
	);
});

export function openAgentConnections(machineId: string): void {
	ModalCommands.pushWithKey(
		ModalCommands.modal(() => (
			<AgentConnectionsDialog machineId={machineId} onClose={() => ModalCommands.popWithKey(KEY)} />
		)),
		KEY,
	);
}
